// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { Stack } from 'expo-router';
import { Linking, Pressable, ScrollView, StyleSheet } from 'react-native';

import { Text, View } from '@/components/Themed';
import { useI18n } from '@/i18n';

/**
 * The privacy policy, served at goffy.app/privacy.
 *
 * It lives in the app rather than a CMS so it ships and versions with the code
 * it describes: when the app starts collecting something new, this is in the
 * same diff. Google Play requires a public URL for it on the store listing.
 *
 * The text is in the translation files like everything else, so it reads in the
 * player's own language. Keep it short — a policy nobody finishes protects
 * nobody. Change LAST_UPDATED whenever the substance changes.
 */
const LAST_UPDATED = '2026-08-07';

const SECTIONS = [
  ['privacy.collectH', 'privacy.collectP'],
  ['privacy.locationH', 'privacy.locationP'],
  ['privacy.sharingH', 'privacy.sharingP'],
  ['privacy.storageH', 'privacy.storageP'],
  ['privacy.rightsH', 'privacy.rightsP'],
] as const;

export default function PrivacyPolicyScreen() {
  const { t } = useI18n();

  return (
    <>
      <Stack.Screen options={{ title: t('privacy.title') }} />
      <ScrollView contentContainerStyle={styles.container}>
        <Text style={styles.title}>{t('privacy.title')}</Text>
        <Text style={styles.updated}>{t('privacy.updated', { date: LAST_UPDATED })}</Text>
        <Text style={styles.intro}>{t('privacy.intro')}</Text>

        {SECTIONS.map(([heading, body]) => (
          <View key={heading} style={styles.section}>
            <Text style={styles.heading}>{t(heading)}</Text>
            <Text style={styles.paragraph}>{t(body)}</Text>
          </View>
        ))}

        {/* The address is the route for data-copy and can't-sign-in deletion requests, so it is
            tappable rather than something to copy out by hand. */}
        <View style={styles.section}>
          <Text style={styles.heading}>{t('privacy.contactH')}</Text>
          <Pressable
            accessibilityRole="link"
            testID="privacy-contact-mail"
            onPress={() => Linking.openURL(`mailto:${t('privacy.contactP')}`)}
          >
            <Text style={[styles.paragraph, styles.link]}>{t('privacy.contactP')}</Text>
          </Pressable>
        </View>
      </ScrollView>
    </>
  );
}

const styles = StyleSheet.create({
  container: {
    padding: 20,
    paddingBottom: 48,
    maxWidth: 720,
    alignSelf: 'center',
    width: '100%',
  },
  title: {
    fontSize: 26,
    fontWeight: '700',
  },
  updated: {
    fontSize: 14,
    opacity: 0.7,
    marginTop: 2,
    marginBottom: 16,
  },
  intro: {
    fontSize: 16,
    lineHeight: 24,
    marginBottom: 20,
  },
  section: {
    marginBottom: 18,
  },
  heading: {
    fontSize: 17,
    fontWeight: '600',
    marginBottom: 6,
  },
  paragraph: {
    fontSize: 16,
    lineHeight: 24,
  },
  link: {
    textDecorationLine: 'underline',
  },
});
